import React, { useState, useEffect, useCallback } from 'react';
import { ApiClient, AuthTokens, SimulationResults } from '../lib/ApiClient';
import CreditCardConnector from './CreditCardConnector';


interface StreamlinedSimulationProps {
  apiUrl: string;
}

const StreamlinedSimulation: React.FC<StreamlinedSimulationProps> = ({ apiUrl }) => {
  const [step, setStep] = useState<'connect' | 'running' | 'results'>('connect');
  const [authTokens, setAuthTokens] = useState<AuthTokens | null>(null);
  const [simulationId, setSimulationId] = useState<string | null>(null);
  const [results, setResults] = useState<SimulationResults | null>(null);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [connectedAndMatched, setConnectedAndMatched] = useState<number>(0);
  const [canSimulate, setCanSimulate] = useState<boolean>(false);
  const [allEnrollmentsReady, setAllEnrollmentsReady] = useState<boolean>(false);
  const [shareState, setShareState] = useState<'idle' | 'copying' | 'copied'>('idle');
  const [deleteState, setDeleteState] = useState<'idle' | 'confirming' | 'deleting'>('idle');

  // Initialize ApiClient with reset callback
  const resetAppState = useCallback(() => {
    console.log('Resetting app state due to authentication error');
    setAuthTokens(null);
    setStep('connect');
    setSimulationId(null);
    setError('Session expired. Please start over.');
    setResults(null);
    setConnectedAndMatched(0);
    setIsLoading(false);
  }, []);

  const [apiClient] = useState(() => new ApiClient(apiUrl, resetAppState));

  // Initialize and load existing data
  useEffect(() => {
    loadExistingData();

    // Check for error parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    if (errorParam === 'share_not_found') {
      setError('Shared link not found or has expired. You can start your own analysis below.');
      // Clear the URL parameter
      window.history.replaceState({}, '', '/simulation');
    }
  }, []);

  const loadExistingData = async () => {
    const hasValidTokens = await apiClient.loadTokensFromStorage();
    if (hasValidTokens) {
      const tokens = apiClient.getAuthTokens();
      if (tokens) {
        setAuthTokens(tokens);

        // Check for saved simulation
        const savedSimulationId = localStorage.getItem('currentSimulationId');
        if (savedSimulationId) {
          try {
            // Check if it's a completed simulation
            const completedSimulationId = localStorage.getItem('completedSimulationId');
            if (completedSimulationId === savedSimulationId) {
              setIsLoading(true);
              setSimulationId(savedSimulationId); // Set simulationId before fetching results
              await fetchResults(savedSimulationId);
              return;
            } else {
              // Continue with existing simulation
              setSimulationId(savedSimulationId);
            }
          } catch (err: any) {
            console.error('Failed to load saved simulation:', err);
            localStorage.removeItem('currentSimulationId');
            localStorage.removeItem('completedSimulationId');
            // If loading existing failed, create new one
            await createNewSimulation();
          }
        } else {
          // If no existing simulation, create a new one
          await createNewSimulation();
        }
      }
    } else {
      // No auth tokens - clear any existing simulation IDs since they belong to a different user
      localStorage.removeItem('currentSimulationId');
      localStorage.removeItem('completedSimulationId');

      // Create new simulation with new anonymous user
      await createNewSimulation();
    }
  };

  const createNewSimulation = async () => {
    try {
      // Ensure we have auth tokens first
      let currentTokens = authTokens || apiClient.getAuthTokens();
      if (!currentTokens) {
        const tokens = await apiClient.createAnonymousUser('simulation');
        if (tokens) {
          setAuthTokens(tokens);
          currentTokens = tokens; // Use the new tokens immediately
        } else {
          throw new Error('Failed to create auth tokens');
        }
      }

      const simulationData = await apiClient.createSimulation();
      const newSimulationId = simulationData.simulation_id;
      setSimulationId(newSimulationId);
      localStorage.setItem('currentSimulationId', newSimulationId);
      console.log('Created new simulation:', newSimulationId);
    } catch (err) {
      console.error('Error creating simulation:', err);
      setError('Failed to initialize simulation');
    }
  };

  const handleConnectionsChange = (connectedAndMappedCount: number, totalConnectedCount: number, unmappableCount: number, allEnrollmentsReadyForAnalysis: boolean = false) => {
    setConnectedAndMatched(connectedAndMappedCount);
    setAllEnrollmentsReady(allEnrollmentsReadyForAnalysis);

    // Allow simulation if we have 2+ mapped accounts AND all mappable accounts are mapped
    // Unmappable accounts (no available cards) don't block simulation
    // Enrollment sync status will be handled during the processing step
    const mappableCount = totalConnectedCount - unmappableCount;
    const allMappableAreMapped = connectedAndMappedCount === mappableCount;
    const canRun = connectedAndMappedCount >= 2 && allMappableAreMapped;
    setCanSimulate(canRun);
  };

  const runSimulation = async () => {
    if (!canSimulate || !simulationId) return;

    try {
      setStep('running');
      setIsLoading(true);

      // Start the simulation (enqueues background job)
      const data = await apiClient.runSimulation(simulationId);

      // Poll until simulation is completed
      await pollForCompletion(simulationId);
    } catch (err: any) {
      setError(err.message || 'Failed to run simulation');
      setStep('connect');
      setIsLoading(false);
    }
  };

  const pollForCompletion = async (simulationId: string) => {
    const maxAttempts = 60; // 5 minutes max (5 second intervals)
    let attempts = 0;

    const poll = async (): Promise<void> => {
      try {
        attempts++;
        console.log(`Polling simulation status (attempt ${attempts}/${maxAttempts})`);

        const simulationData = await apiClient.getSimulation(simulationId);
        const status = simulationData.status;

        console.log(`Simulation status: ${status}`);

        if (status === 'completed') {
          // Simulation is done, fetch results and show them
          await fetchResults(simulationId);
          return;
        } else if (status === 'failed') {
          throw new Error('Simulation failed to complete');
        } else if (attempts >= maxAttempts) {
          console.log('Simulation timed out - starting over');
          await startOver();
          return;
        } else {
          // Still processing, poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch (err: any) {
        console.error('Error polling simulation:', err);
        throw err;
      }
    };

    await poll();
  };

  const fetchResults = async (simulationId: string) => {
    try {
      const data = await apiClient.getSimulationResults(simulationId);
      setResults(data.simulation);
      setStep('results');
      setIsLoading(false);
      localStorage.setItem('completedSimulationId', simulationId);
    } catch (err: any) {
      setError('Failed to fetch results');
      setStep('connect');
      setIsLoading(false);
      throw err;
    }
  };

  const startOver = async () => {
    // Clear localStorage first
    localStorage.removeItem('completedSimulationId');
    localStorage.removeItem('currentSimulationId');

    // Reset UI state
    setStep('connect');
    setSimulationId(null);
    setError('');
    setResults(null);
    setConnectedAndMatched(0);
    setIsLoading(false);

    // Keep existing auth tokens to reuse the same user
    // Create a new simulation for the existing user
    await createNewSimulation();
  };

  // Calculate progress

  const getStepProgress = () => {
    switch (step) {
      case 'connect': return 0;
      case 'running': return 50;
      case 'results': return 100;
      default: return 0;
    }
  };

  const getScoreGrade = (score: number) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleShare = async () => {
    if (!simulationId) {
      console.error('No simulation ID available for sharing');
      return;
    }

    if (shareState !== 'idle') return;

    setShareState('copying');
    try {
      const shareData = await apiClient.shareSimulation(simulationId);
      await navigator.clipboard.writeText(shareData.share_url);

      setShareState('copied');
      setTimeout(() => setShareState('idle'), 3000);
    } catch (err: any) {
      console.error('Failed to share simulation:', err);
      setShareState('idle');
      setError('Failed to create share link');
    }
  };

  const handleDeleteData = async () => {
    if (deleteState === 'confirming') {
      setDeleteState('deleting');
      try {
        await apiClient.deleteUserData();

        // Clear all local storage
        localStorage.clear();

        // Reset all state
        setAuthTokens(null);
        setSimulationId(null);
        setResults(null);
        setStep('connect');
        setConnectedAndMatched(0);
        setCanSimulate(false);
        setShareState('idle');
        setDeleteState('idle');

        // Clear API client tokens
        apiClient.clearAuthTokens();

        // Show success message using existing banner pattern
        setError('Your data has been permanently deleted. You can start a new analysis below.');
      } catch (err: any) {
        console.error('Failed to delete user data:', err);
        setError('Failed to delete data. Please try again.');
        setDeleteState('idle');
      }
    } else {
      setDeleteState('confirming');
      // Auto-reset confirmation after 5 seconds
      setTimeout(() => {
        if (deleteState === 'confirming') setDeleteState('idle');
      }, 5000);
    }
  };

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Poppins, sans-serif',
      minHeight: '800px'
    }}>
      {/* Progress Bar */}
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '15px',
        marginBottom: '30px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', position: 'relative' }}>
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '0',
            right: '0',
            height: '2px',
            background: '#ddd',
            zIndex: -1
          }}>
            <div style={{
              height: '100%',
              background: '#00b894',
              transition: 'width 0.5s',
              width: `${getStepProgress()}%`
            }} />
          </div>

          {[
            { label: 'Connect & Match Cards', step: 'connect' },
            { label: 'Run Simulation', step: 'running' },
            { label: 'View Results', step: 'results' }
          ].map((stepInfo, index) => (
            <div key={stepInfo.step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: step === stepInfo.step ? '#0984e3' :
                           getStepProgress() > index * 50 ? '#00b894' : '#ddd',
                color: step === stepInfo.step || getStepProgress() > index * 50 ? 'white' : '#636e72',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                marginBottom: '10px',
                transition: 'all 0.3s'
              }}>
                {getStepProgress() > index * 50 ? '✓' : index + 1}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#636e72',
                textAlign: 'center',
                maxWidth: '80px'
              }}>
                {stepInfo.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#e17055',
          color: 'white',
          padding: '15px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Step 1: Connect & Match Cards */}
      {step === 'connect' && (
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3436', marginBottom: '15px' }}>
            Connect & Match Your Credit Cards
          </h2>
          <p style={{ fontSize: '16px', color: '#636e72', marginBottom: '30px', lineHeight: '1.6' }}>
            Connect each of your credit cards individually and tell us which specific card it is.
            We'll analyze your spending to show you how much more you could be earning in rewards.
          </p>

          <CreditCardConnector
            apiClient={apiClient}
            authTokens={authTokens}
            setAuthTokens={setAuthTokens}
            simulationId={simulationId}
            onConnectionsChange={handleConnectionsChange}
            setError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onStartOver={startOver}
          />

          {/* Continue Section */}
          <div style={{
            marginTop: '40px',
            paddingTop: '30px',
            borderTop: '2px solid #eee',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '16px', color: '#636e72', marginBottom: '5px' }}>
                Connected & matched cards:
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#00b894' }}>
                {connectedAndMatched}
              </div>
            </div>
            <button
              onClick={runSimulation}
              disabled={!canSimulate || isLoading}
              style={{
                background: canSimulate ? '#00b894' : '#ddd',
                color: 'white',
                padding: '18px 40px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: '600',
                cursor: canSimulate ? 'pointer' : 'not-allowed',
                opacity: canSimulate ? 1 : 0.5
              }}
            >
              Run My Credit Card Analysis
            </button>
            <p style={{ color: '#636e72', fontSize: '14px', marginTop: '10px' }}>
              {connectedAndMatched < 2
                ? 'Connect and map at least 2 credit cards to run simulation'
                : 'Ready to run analysis!'}
            </p>
          </div>

        </div>
      )}

      {/* Step 2: Running Simulation */}
      {step === 'running' && (
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3436', marginBottom: '15px' }}>
            Analyzing Your Rewards...
          </h2>
          <p style={{ fontSize: '16px', color: '#636e72', marginBottom: '30px' }}>
            We're crunching the numbers on your spending patterns and comparing them against optimal credit card usage.
          </p>

          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #ddd',
            borderTop: '4px solid #0984e3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '20px auto'
          }} />

          <p style={{ color: '#636e72', marginTop: '20px' }}>
            This usually takes 10-15 seconds
          </p>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 'results' && results && results.results && results.results.optimization_score !== undefined && (
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
        }}>
          {/* Social Share Card */}
          <div style={{
            background: 'linear-gradient(135deg, #a29bfe, #74b9ff)',
            color: 'white',
            padding: '40px',
            borderRadius: '20px',
            textAlign: 'center',
            margin: '0 auto',
            maxWidth: '500px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <h3 style={{ fontSize: '24px' }}>My Stashr.ai Credit Card Optimization Score</h3>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <div style={{
                fontSize: '56px',
                fontWeight: '700',
                lineHeight: '1.1',
                height: 'auto'
              }}>
                {results.results.optimization_score}<small style={{ fontSize: '28px' }}>/100</small>
              </div>
              <div style={{
                fontSize: '48px',
                fontWeight: '700',
                color: '#ff7675',
                lineHeight: '1.1',
                height: 'auto'
              }}>
                {getScoreGrade(results.results.optimization_score)}
              </div>
            </div>

            <div style={{ fontSize: '18px', opacity: 0.9 }}>
              "I got a {getScoreGrade(results.results.optimization_score)} on my credit card optimization! 😬 Missing out on {formatCurrency(results.results.missed_opportunity)} in rewards."
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '600' }}>
                  {results.uncategorized_summary?.transaction_count || 0}
                </div>
                <div style={{ fontSize: '11px', opacity: '0.8' }}>TRANSACTIONS</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '600' }}>12 MONTHS</div>
                <div style={{ fontSize: '11px', opacity: '0.8' }}>ANALYZED</div>
              </div>
            </div>

            <p style={{ fontSize: '14px', opacity: 0.8 }}>
              Can you beat my score? Try Stashr.ai
            </p>

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={handleShare}
                disabled={shareState !== 'idle'}
                style={{
                  background: shareState === 'copied' ? '#00b894' : '#fdcb6e',
                  color: 'white',
                  padding: '15px 30px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: shareState === 'idle' ? 'pointer' : 'default',
                  opacity: shareState === 'copying' ? 0.7 : 1,
                  transition: 'all 0.2s ease'
                }}>
                {shareState === 'copying' ? 'Creating Link...' :
                 shareState === 'copied' ? '✓ Link Copied!' :
                 'Share My Score'}
              </button>
              <button style={{
                background: 'transparent',
                border: '2px solid white',
                color: 'white',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
                Challenge a Friend
              </button>
            </div>
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            marginTop: '40px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={async () => {
                localStorage.removeItem('completedSimulationId');
                setStep('connect');
                setResults(null);
                setError('');
                // Keep the existing simulation - simulationId and authTokens stay the same
                // This will allow CreditCardConnector to reload existing enrollments
                console.log('Run New Analysis clicked - keeping simulationId:', simulationId);
              }}
              style={{
                background: '#0984e3',
                color: 'white',
                padding: '15px 30px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '200px'
              }}
            >
              Run New Analysis
            </button>
            <button
              onClick={startOver}
              style={{
                background: 'transparent',
                border: '2px solid #636e72',
                color: '#636e72',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '200px'
              }}
            >
              Start Over
            </button>
            <button
              onClick={handleDeleteData}
              disabled={deleteState === 'deleting'}
              style={{
                background: deleteState === 'confirming' ? '#e17055' : 'transparent',
                border: deleteState === 'confirming' ? '2px solid #e17055' : '2px solid #e17055',
                color: deleteState === 'confirming' ? 'white' : '#e17055',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: deleteState === 'deleting' ? 'not-allowed' : 'pointer',
                opacity: deleteState === 'deleting' ? 0.6 : 1,
                transition: 'all 0.2s ease',
                width: '200px'
              }}
            >
              {deleteState === 'deleting' ? 'Deleting...' :
               deleteState === 'confirming' ? 'Click Again to Confirm' :
               'Delete My Data'}
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default StreamlinedSimulation;
